import { useCallback, useEffect, useRef, useState } from "react";

/** Distance from the bottom (px) within which the view still counts as
 *  "pinned". A strict `scrollTop === scrollHeight` check flickers while stream
 *  deltas reflow the transcript, so a small threshold keeps a near-bottom
 *  reader followed instead of stranding them one line up. */
const PIN_THRESHOLD_PX = 64;

/**
 * Keep a scroll container glued to its bottom **only while the user is already
 * there**. The moment they scroll up to re-read history, following stops and
 * their position is left alone; scrolling back down re-pins. New content that
 * arrives while unpinned surfaces a "jump to latest" affordance rather than
 * yanking the viewport.
 *
 * Returns a **callback ref** to attach to the scrollable element (`setNode`),
 * the `onScroll` handler to wire onto it, whether the jump affordance should
 * show, and an imperative `jumpToBottom`. `resetKey` (e.g. the thread id)
 * re-pins and jumps whenever it changes, so opening a different conversation
 * lands on its latest message.
 *
 * A callback ref (not a returned `RefObject`) keeps the hook's element handle
 * private — the consumer never touches a ref during render.
 */
export function useStickToBottom<T extends HTMLElement>(resetKey?: string) {
  const elRef = useRef<T | null>(null);
  // Pinned state lives in a ref, not React state: the MutationObserver callback
  // reads it synchronously on every content change and must see the current
  // value without re-subscribing the observer.
  const pinned = useRef(true);
  const observerRef = useRef<MutationObserver | null>(null);
  const [showJump, setShowJump] = useState(false);

  const nearBottom = (el: HTMLElement) =>
    el.scrollHeight - el.scrollTop - el.clientHeight <= PIN_THRESHOLD_PX;

  const jumpToBottom = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    pinned.current = true;
    setShowJump(false);
  }, []);

  // User-driven scroll updates the pin: leaving the bottom unpins (and, when
  // content sits below the fold, surfaces the jump button); returning re-pins.
  const onScroll = useCallback(() => {
    const el = elRef.current;
    if (!el) return;
    const atBottom = nearBottom(el);
    pinned.current = atBottom;
    setShowJump(!atBottom);
  }, []);

  // Callback ref: wire the observer when the node mounts, tear it down when it
  // unmounts. The observer follows content growth — new messages and streaming
  // text deltas both mutate the transcript DOM — but only while pinned. Setting
  // scrollTop doesn't mutate the DOM, so it can't re-trigger the observer.
  const setNode = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    elRef.current = node;
    if (!node) return;
    node.scrollTop = node.scrollHeight; // land at the bottom on mount
    const observer = new MutationObserver(() => {
      if (pinned.current) node.scrollTop = node.scrollHeight;
      else setShowJump(true);
    });
    observer.observe(node, { childList: true, subtree: true, characterData: true });
    observerRef.current = observer;
  }, []);

  // Land at the bottom whenever the conversation changes (thread switch).
  useEffect(() => {
    jumpToBottom();
  }, [resetKey, jumpToBottom]);

  return { setNode, onScroll, showJump, jumpToBottom };
}
